import { cn } from '@quadralize/utils'
import { Star } from '@solar-icons/react'
import * as React from 'react'
import { useState } from 'react'

const FULL_PERCENT = 100
const EMPTY_VALUE = 0
const UNIT_VALUE = 1

export interface RatingProps extends Omit<React.HTMLAttributes<HTMLFieldSetElement>, 'onChange'> {
  value?: number
  defaultValue?: number
  max?: number
  readOnly?: boolean
  onChange?: (value: number) => void
  size?: 'sm' | 'md' | 'lg'
}

export const Rating = React.forwardRef<HTMLFieldSetElement, RatingProps>(
  (
    {
      className,
      value: controlledValue,
      defaultValue = 0,
      max = 5,
      readOnly = false,
      onChange,
      size = 'md',
      ...props
    },
    ref,
  ) => {
    const [localValue, setLocalValue] = useState(defaultValue)
    const [hoverValue, setHoverValue] = useState<number | null>(null)

    const value = controlledValue !== undefined ? controlledValue : localValue
    const displayValue = hoverValue !== null ? hoverValue : value

    const handleMouseEnter = (index: number) => {
      if (!readOnly) {
        setHoverValue(index)
      }
    }

    const handleMouseLeave = () => {
      if (!readOnly) {
        setHoverValue(null)
      }
    }

    const handleClick = (index: number) => {
      if (!readOnly) {
        setLocalValue(index)
        onChange?.(index)
      }
    }

    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-6 h-6',
      lg: 'w-8 h-8',
    }

    const generatedId = React.useId()

    return (
      <fieldset
        ref={ref}
        className={cn(
          'flex items-center gap-1 border-none p-0 m-0',
          readOnly ? 'cursor-default' : 'cursor-pointer',
          className,
        )}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <legend className="sr-only">Avaliação</legend>
        {Array.from({ length: max }).map((_, i) => {
          const index = i + 1
          const fillPercentage =
            Math.min(Math.max(displayValue - (index - 1), EMPTY_VALUE), UNIT_VALUE) * FULL_PERCENT
          const isFull = fillPercentage >= FULL_PERCENT
          const isEmpty = fillPercentage <= EMPTY_VALUE
          const isPartial = !isFull && !isEmpty
          const inputId = `${generatedId}-${index}`

          return (
            <div key={inputId} className="relative flex items-center justify-center">
              <input
                type="radio"
                id={inputId}
                name={generatedId}
                value={index}
                checked={Math.ceil(value) === index} // Check the nearest upper integer for active state logic in radio
                onChange={() => handleClick(index)}
                disabled={readOnly}
                className="sr-only peer"
              />
              <label
                htmlFor={inputId}
                onMouseEnter={() => handleMouseEnter(index)}
                className={cn(
                  'relative flex items-center justify-center outline-none rounded-sm transition-transform duration-200',
                  !readOnly && 'hover:scale-110 cursor-pointer active:scale-95',
                  'peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2',
                )}
                aria-label={`${index} estrela${index > 1 ? 's' : ''}`}
              >
                {/* Background Star (Empty) - Always rendered for partial/empty, or underneath full */}
                <Star
                  className={cn(
                    'text-text-tertiary transition-colors duration-200',
                    sizeClasses[size],
                    isFull && 'text-warning', // If full, base can be colored too or just covered. Text-warning makes sure simple transitions work well.
                  )}
                />

                {/* Overlay Star (Filled) - For Partial or Full */}
                {(isPartial || isFull) && (
                  <div
                    className="absolute inset-0 overflow-hidden"
                    style={{ width: `${fillPercentage}%` }}
                  >
                    <Star
                      weight="Bold"
                      className={cn('text-warning whitespace-nowrap', sizeClasses[size])}
                    />
                  </div>
                )}
              </label>
            </div>
          )
        })}
      </fieldset>
    )
  },
)

Rating.displayName = 'Rating'
